/**
 * Theme tokens and base CSS reset for the POLST widget shadow DOM.
 *
 * Everything here is inlined into the shadow root — there is no global
 * stylesheet injection, and no external CSS request. The token names
 * are the public surface consumers reference when overriding branding
 * (brand theme pipeline in POL-648+).
 *
 * Token naming follows the `--polst-*` namespace so third-party host
 * pages cannot accidentally clash with them.
 */

/** Theme resolution mode. `auto` follows `prefers-color-scheme`. */
export type ThemeMode = "light" | "dark" | "auto";

/**
 * CSS custom property names exposed on `:host`. Consumers read these
 * to understand the token contract.
 */
export const THEME_TOKENS = [
  "--polst-bg",
  "--polst-fg",
  "--polst-muted-fg",
  "--polst-accent",
  "--polst-accent-fg",
  "--polst-button-hover",
  "--polst-error-fg",
  "--polst-radius",
  "--polst-spacing-xs",
  "--polst-spacing-sm",
  "--polst-spacing-md",
  "--polst-spacing-lg",
  "--polst-spacing-xl",
  "--polst-font-stack",
] as const;

export type ThemeTokenName = (typeof THEME_TOKENS)[number];

/**
 * Base reset applied inside the shadow root. This is narrow on purpose:
 * `:host` handles box-sizing / margin / font defaults, and a scoped
 * universal selector re-applies box-sizing to descendants so any HTML
 * the renderer injects behaves consistently. Host-page CSS cannot
 * reach across the shadow boundary, so we do not need an "eric meyer"
 * style sledgehammer reset.
 */
const RESET_CSS = `
:host {
  all: initial;
  display: block;
  box-sizing: border-box;
  margin: 0;
  padding: 0;
  color: var(--polst-fg);
  background: transparent;
  font-family: var(--polst-font-stack);
  font-size: 16px;
  line-height: 1.5;
  text-align: left;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color-scheme: light dark;
  contain: content;
}
:host([hidden]) { display: none; }
*, *::before, *::after { box-sizing: inherit; }
`;

/**
 * Light theme token values. These are defaults; the brand-theme
 * pipeline (POL-648+) will layer overrides on top via inline
 * `style="--polst-accent: #f00"` on the shadow host, which wins over
 * the defaults declared on `:host`.
 */
const LIGHT_TOKENS = `
:host {
  --polst-bg: #ffffff;
  --polst-fg: #111827;
  --polst-muted-fg: #4b5563;
  --polst-accent: #2563eb;
  --polst-accent-fg: #ffffff;
  --polst-button-hover: #1d4ed8;
  --polst-error-fg: #b91c1c;
  --polst-radius: 8px;
  --polst-spacing-xs: 4px;
  --polst-spacing-sm: 8px;
  --polst-spacing-md: 12px;
  --polst-spacing-lg: 16px;
  --polst-spacing-xl: 24px;
  --polst-font-stack: system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
}
`;

/**
 * Dark theme token values. Applied in two ways:
 *   1. `@media (prefers-color-scheme: dark)` — default when the host
 *      sets `data-theme="auto"` (or omits it entirely).
 *   2. `:host([data-theme="dark"])` — runtime override that wins
 *      regardless of the user's OS preference.
 *
 * `:host([data-theme="light"])` forces light and neutralises the
 * media query for that instance.
 */
const DARK_TOKENS_BODY = `
  --polst-bg: #0b0d12;
  --polst-fg: #f3f4f6;
  --polst-muted-fg: #b4bac3;
  --polst-accent: #60a5fa;
  --polst-accent-fg: #0b0d12;
  --polst-button-hover: #93c5fd;
  --polst-error-fg: #fca5a5;
`;

const THEME_CSS = `
${LIGHT_TOKENS}

@media (prefers-color-scheme: dark) {
  :host(:not([data-theme="light"])) {
${DARK_TOKENS_BODY}
  }
}

:host([data-theme="dark"]) {
${DARK_TOKENS_BODY}
}
`;

/** Full stylesheet content applied inside the shadow root. */
export const SHADOW_CSS = `${RESET_CSS}\n${THEME_CSS}`.trim();

/**
 * Apply the widget stylesheet to a shadow root. Prefers constructable
 * stylesheets (single shared CSSStyleSheet, zero parse cost per
 * instance) and falls back to a `<style>` element for runtimes that do
 * not implement it (jsdom, older Safari).
 *
 * Returns a teardown callback the caller can invoke from `unmount()`
 * to detach the stylesheet and let the shadow root be garbage-collected.
 */
export function installShadowStyles(root: ShadowRoot): () => void {
  // Feature-detect constructable stylesheets. `root.adoptedStyleSheets`
  // must be a writable array, and the global CSSStyleSheet constructor
  // must be usable. Some environments (jsdom, older webkit) expose
  // `CSSStyleSheet` but refuse `new CSSStyleSheet()` or refuse
  // `.replaceSync()`, so we also guard on those.
  const canUseConstructable =
    typeof CSSStyleSheet !== "undefined" &&
    "adoptedStyleSheets" in root &&
    Array.isArray((root as unknown as { adoptedStyleSheets: unknown }).adoptedStyleSheets);

  if (canUseConstructable) {
    try {
      const sheet = new CSSStyleSheet();
      // replaceSync is synchronous and throws on parse failure, which
      // is what we want during dev.
      (sheet as unknown as { replaceSync: (css: string) => void }).replaceSync(SHADOW_CSS);
      const current = (root as unknown as { adoptedStyleSheets: CSSStyleSheet[] })
        .adoptedStyleSheets;
      (root as unknown as { adoptedStyleSheets: CSSStyleSheet[] }).adoptedStyleSheets = [
        ...current,
        sheet,
      ];
      return () => {
        const now = (root as unknown as { adoptedStyleSheets: CSSStyleSheet[] })
          .adoptedStyleSheets;
        (root as unknown as { adoptedStyleSheets: CSSStyleSheet[] }).adoptedStyleSheets =
          now.filter((s) => s !== sheet);
      };
    } catch {
      // Fall through to <style> fallback.
    }
  }

  const styleEl = document.createElement("style");
  styleEl.setAttribute("data-polst-widget-styles", "");
  styleEl.textContent = SHADOW_CSS;
  root.appendChild(styleEl);
  return () => {
    if (styleEl.parentNode === root) {
      root.removeChild(styleEl);
    }
  };
}

/**
 * Normalise an arbitrary string into a supported theme mode. Unknown
 * values default to `"auto"`.
 */
export function normalizeThemeMode(value: unknown): ThemeMode {
  if (value === "light" || value === "dark" || value === "auto") return value;
  return "auto";
}

// ─── POL-644: Brand theme enum → CSS value maps ────────────────────────────

/**
 * Brand-theme `radius` enum → `--polst-radius` value. Mirrors the design
 * scale documented in POL-642 / POL-647 (manager UI). `FULL` maps to a
 * very large pill radius rather than `9999px` so widget tiles preserve
 * a recognisable card shape; consumers wanting "true" pill buttons
 * still get the effect because the pill sits inside a card.
 */
export const BRAND_RADIUS_PX: Record<"NONE" | "SM" | "MD" | "LG" | "FULL", string> = {
  NONE: "0px",
  SM: "4px",
  MD: "8px",
  LG: "16px",
  FULL: "9999px",
};

/**
 * Brand-theme `density` enum → spacing scale. `COMFORTABLE` matches the
 * defaults in `LIGHT_TOKENS`; `COMPACT` uniformly multiplies the scale
 * by ~0.66 so brands with information-dense feeds can fit more onscreen.
 */
export const BRAND_DENSITY_SCALES: Record<
  "COMPACT" | "COMFORTABLE",
  { xs: string; sm: string; md: string; lg: string; xl: string }
> = {
  COMFORTABLE: {
    xs: "4px",
    sm: "8px",
    md: "12px",
    lg: "16px",
    xl: "24px",
  },
  COMPACT: {
    xs: "2px",
    sm: "6px",
    md: "8px",
    lg: "12px",
    xl: "16px",
  },
};

/**
 * Brand-theme `fontStack` enum → CSS `font-family` value. The `BRAND`
 * stack is intentionally identical to `SYSTEM` here — the brand-injected
 * `@font-face` adds the actual brand family at the front of the list
 * dynamically (see `applyBrandTheme` in `brand-theme.ts`).
 */
export const BRAND_FONT_STACKS: Record<"SYSTEM" | "SERIF" | "MONO" | "BRAND", string> = {
  SYSTEM:
    'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  SERIF: 'Georgia, "Times New Roman", Times, serif',
  MONO: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
  BRAND:
    'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
};
